<template>
    <component :is="modal ? 'b-modal' : 'div'" id="price-test-modal" title="Drug Price Test" centered
        @hide="$emit('hide')" no-fade hide-footer size="lg">

        <b-form @submit.prevent="getPrice">
            <SearchComponent v-if="showSchedulePicker" v-model="selectedPriceSchedule" label="Price Schedule"
                search-u-r-l="/PriceSchedule" prepopulate clearable />
            <b-form-group v-else-if="priceSchedule" label="Price Schedule:">
                <b-input :value="priceSchedule.name" disabled />
            </b-form-group>

            <search-component v-model="drug" label="Drug" :params="{ onlyActive: true }" search-u-r-l="/api/Drug" />

            <!-- Member Tier Selection -->
            <b-form-group v-if="memberTiers.length > 0">
                <label>
                    Member Tier (Optional)
                    <HelpPopup>
                        Select a member tier to simulate member pricing. This will use the member tier's price schedule instead of the base price schedule for price calculations.
                    </HelpPopup>
                </label>
                <select 
                    v-model.number="selectedMemberTierId"
                    class="form-control"
                >
                    <option :value="null">No member tier (standard pricing)</option>
                    <option 
                        v-for="tier in memberTiers" 
                        :key="tier.id"
                        :value="tier.id"
                    >
                        {{ tier.tierName }} - {{ tier.memberPriceSchedule?.name || 'No Price Schedule' }}
                    </option>
                </select>
            </b-form-group>

            <b-form-group label="Quantity:">
                <b-form-input type="number" v-model.number="quantity" min="0" step=".001" />
            </b-form-group>

            <b-button type="submit" :disabled="!formIsValid">Get Price</b-button>
        </b-form>

        <div v-if="!error && loaded && priceData">
            <hr>
            
            <!-- Member Tier Info Display -->
            <div v-if="priceData.memberTierInfo" class="alert alert-info mb-3">
                <h6><i class="fas fa-crown"></i> Member Tier Applied</h6>
                <p class="mb-1">
                    <strong>{{ priceData.memberTierInfo.tierName }}</strong> 
                    ({{ priceData.memberTierInfo.stripeProductId }})
                </p>
                <p class="mb-0">
                    Using member price schedule: <strong>{{ priceData.memberTierInfo.memberPriceScheduleName }}</strong>
                </p>
            </div>

            <b-row>
                <b-col>
                    <strong>Total Price:</strong>
                    <h4 class="my-0">
                        {{
            (priceData.priceInfo || priceData).totalPrice.toLocaleString("en-US", {
                style: "currency", currency: "USD",
                minimumFractionDigits: 2
            })
        }}
                    </h4>
                </b-col>
                <b-col>
                    <strong>Unit Price:</strong>
                    <h4 class="my-0">
                        {{ unitPrice.toLocaleString("en-US", {
            style: "currency", currency: "USD",
            minimumFractionDigits: 2
        }) }}
                    </h4>
                </b-col>
            </b-row>

            <hr>
            <h6>Quantity Pricing for {{ drug.productNameShort }}</h6>
            <QuantityPricingComponent :price-schedule="scheduleToUse" :default-drug="drug" :hide-title="true"
                :highlight-quantity="priceData.quantity" :on-add="onAdd" @added="getPrice" @edited="getPrice"
                @removed="getPrice" />
        </div>

        <div v-if="error">
            <hr>
            <b-alert show variant="danger">
                {{ error }}
            </b-alert>
        </div>
    </component>
</template>

<script lang="ts">
import { Drug } from '@/models/Drug/Drug';
import { DrugProduct } from '@/models/Drug/DrugProduct';
import { PriceSchedule } from '@/models/PriceSchedule';
import { PriceScheduleMemberTier } from '@/models/PriceScheduleMemberTier';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import SearchComponent from "@/components/SearchComponent.vue";
import HelpPopup from '@/components/HelpPopup.vue';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import QuantityPricingComponent from "@/components/Drug/QuantityPricingComponent.vue";
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

const lastPriceScheduleIdStorageKey = "price-test-selected-schedule-id";

@Component({
    name: "PriceTestModalComponent",
    components: {
        SearchComponent,
        QuantityPricingComponent,
        HelpPopup
    }
})
export default class PriceTestModalComponent extends Vue {

    @Prop({ type: PriceSchedule }) protected priceSchedule!: PriceSchedule;
    @Prop({ required: false }) onAdd?: Function;
    @Prop({ default: true }) protected modal: boolean = true;

    showSchedulePicker = false;
    selectedPriceSchedule = new PriceSchedule();
    memberTiers: PriceScheduleMemberTier[] = [];
    selectedMemberTierId: number | null = null;

    drug: DrugProduct = new Drug();
    quantity: number = 0;

    priceData?: any = {};
    error?: string = "";
    loaded: boolean = false;

    mounted() {
        this.$bvModal.show("price-test-modal");
        if (!this.priceSchedule) {
            const lastPriceScheduleId = localStorage.getItem(lastPriceScheduleIdStorageKey);
            if (lastPriceScheduleId) {
                this.selectedPriceSchedule.id = Number(lastPriceScheduleId);
            }
            this.showSchedulePicker = true;
        }
    }

    get scheduleToUse() {
        return this.priceSchedule ? this.priceSchedule : this.selectedPriceSchedule;
    }

    get unitPrice() {
        const totalPrice = this.priceData.priceInfo ? this.priceData.priceInfo.totalPrice : this.priceData.totalPrice;
        return totalPrice / this.quantity;
    }

    async getPrice() {
        if (!this.drug.id) {
            this.priceData = undefined;
            this.error = undefined;
            return;
        }

        try {
            const params: any = {
                priceScheduleId: this.scheduleToUse.id,
                drugId: this.drug.id,
                quantity: this.quantity,
                drugSource: this.drug.source,
            }

            // Add member tier if selected
            if (this.selectedMemberTierId) {
                params.memberTierId = this.selectedMemberTierId;
            }

            const config = {
                params: params
            } as AxiosRequestConfig;

            const priceData = (await axios.get(`/api/PriceSchedule/price-preview`, config)).data;
            this.priceData = priceData;
            this.error = "";
            this.loaded = true;
        } catch (err) {
            this.error = ((err as AxiosError)?.response?.data as any).message ?? "There was an unknown error";
            this.$notification(NotificationOptions.error(err));
        }
    }

    get formIsValid() {
        return this.quantity > 0 && this.drug?.id;
    }

    @Watch("selectedPriceSchedule")
    async onSelectedPriceScheduleChanged() {
        localStorage.setItem(lastPriceScheduleIdStorageKey, this.selectedPriceSchedule.id.toString());
        await this.loadMemberTiers();
    }

    @Watch('scheduleToUse', { immediate: true })
    async onScheduleChanged() {
        if (this.scheduleToUse?.id) {
            await this.loadMemberTiers();
        }
    }

    async loadMemberTiers() {
        if (!this.scheduleToUse?.id) {
            this.memberTiers = [];
            return;
        }

        try {
            const response = await axios.get(`/api/priceschedule/${this.scheduleToUse.id}/member-tiers`);
            this.memberTiers = response.data;
            // Reset selected tier when price schedule changes
            this.selectedMemberTierId = null;
        } catch (error) {
            console.error('Error loading member tiers:', error);
            this.memberTiers = [];
        }
    }

}

</script>

<style scoped>
::v-deep(tr.table-success) {
    font-weight: bolder;
}
</style>
